import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { formatIndianDate } from '../utils/format';

const Clients = ({ user }) => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: ''
  });

  const [editData, setEditData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    is_active: 1
  });

  const [statusFilter, setStatusFilter] = useState('ACTIVE');
  const [searchQ, setSearchQ] = useState('');
  const [statusCounts, setStatusCounts] = useState({ active: 0, inactive: 0 });

  const canManage = useMemo(() => user?.role === 'ADMIN' || user?.role === 'BDM', [user?.role]);

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/clients', {
        params: {
          status: user.role === 'ADMIN' ? statusFilter : undefined,
          q: user.role === 'ADMIN' && searchQ ? searchQ : undefined,
          include_counts: user.role === 'ADMIN' ? true : undefined
        }
      });
      
      const payload = res.data;
      if (payload && typeof payload === 'object' && Array.isArray(payload.items)) {
        setClients(payload.items);
        if (user.role === 'ADMIN' && payload.counts) {
          setStatusCounts({
            active: Number(payload.counts.active) || 0,
            inactive: Number(payload.counts.inactive) || 0
          });
        }
      } else {
        setClients(Array.isArray(payload) ? payload : []);
      }
    } catch (e) {
      console.error('Error fetching clients:', e);
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, [searchQ, statusFilter, user.role]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    if (user.role !== 'ADMIN') return;
    const t = setTimeout(() => {
      fetchClients();
    }, 250);
    return () => clearTimeout(t);
  }, [fetchClients, searchQ, statusFilter, user.role]);

  const handleChange = (e) => {
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleEditChange = (e) => {
    setEditData((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditData({
      name: c.name || '',
      company: c.company || '',
      email: c.email || '',
      phone: c.phone || '',
      is_active: c.is_active ?? 1
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const closeCreate = () => {
    setShowForm(false);
  };

  const createClient = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/clients', formData);
      setShowForm(false);
      setFormData({ name: '', company: '', email: '', phone: '' });
      await fetchClients();
    } catch (e2) {
      console.error('Error creating client:', e2);
    }
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/api/clients/${id}`, {
        ...editData,
        is_active: editData.is_active === '' ? null : Number(editData.is_active)
      });
      setEditingId(null);
      await fetchClients();
    } catch (e) {
      console.error('Error updating client:', e);
    }
  };

  if (!canManage) {
    return <div className="text-center p-8">Insufficient permissions</div>;
  }

  if (loading) {
    return <div className="text-center p-8">Loading clients...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          <h1 className="text-2xl font-bold">Clients</h1>

          {user.role === 'ADMIN' ? (
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                className="status-badge"
                onClick={() => setStatusFilter('ACTIVE')}
                style={{
                  cursor: 'pointer',
                  borderColor: statusFilter === 'ACTIVE' ? 'rgba(40, 167, 69, 0.55)' : 'rgba(255, 255, 255, 0.16)',
                  background: statusFilter === 'ACTIVE' ? 'rgba(40, 167, 69, 0.90)' : 'rgba(40, 167, 69, 0.14)',
                  color: statusFilter === 'ACTIVE' ? '#ffffff' : 'var(--text-secondary)',
                  boxShadow: statusFilter === 'ACTIVE' ? '0 10px 30px rgba(40, 167, 69, 0.28)' : undefined
                }}
              >
                Active {statusCounts.active}
              </button>
              <button
                type="button"
                className="status-badge"
                onClick={() => setStatusFilter('INACTIVE')}
                style={{
                  cursor: 'pointer',
                  borderColor: statusFilter === 'INACTIVE' ? 'rgba(220, 53, 69, 0.55)' : 'rgba(255, 255, 255, 0.16)',
                  background: statusFilter === 'INACTIVE' ? 'rgba(220, 53, 69, 0.90)' : 'rgba(220, 53, 69, 0.14)',
                  color: statusFilter === 'INACTIVE' ? '#ffffff' : 'var(--text-secondary)',
                  boxShadow: statusFilter === 'INACTIVE' ? '0 10px 30px rgba(220, 53, 69, 0.22)' : undefined
                }}
              >
                Inactive {statusCounts.inactive}
              </button>
              <input
                className="glass-input"
                placeholder="Search"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                style={{ width: 220 }}
              />
            </div>
          ) : null}
        </div>
        <button type="button" className="glass-button primary" onClick={() => setShowForm(true)}>
          + New Client
        </button>
      </div>

      {showForm ? (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal" style={{ width: 'min(860px, 100%)' }}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold">Create Client</h3>
              <button type="button" className="glass-button" onClick={closeCreate}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={createClient} className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2">Name</label>
                  <input name="name" value={formData.name} onChange={handleChange} className="glass-input w-full" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Company</label>
                  <input name="company" value={formData.company} onChange={handleChange} className="glass-input w-full" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} className="glass-input w-full" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <input name="phone" value={formData.phone} onChange={handleChange} className="glass-input w-full" />
                </div>
                <div className="modal-footer col-span-2">
                  <button type="button" className="glass-button" onClick={closeCreate}>Cancel</button>
                  <button type="submit" className="glass-button primary">Create</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}

      <div
        style={{
          display: 'grid',
          gap: 16,
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))'
        }}
      >
        {clients.map((c) => (
          <div key={c.id} className="glass-card p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-lg font-semibold truncate">{c.name}</div>
                <div className="text-sm text-secondary truncate">{c.company || ''}</div>
                <div className="text-xs text-tertiary truncate">{c.email || ''}</div>
                <div className="text-xs text-tertiary">{c.phone || ''}</div>
                <div className="text-xs text-tertiary mt-1">Created: {formatIndianDate(c.created_at)}</div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2 justify-end flex-wrap">
                  <span className={`status-badge ${c.is_active ? 'status-active' : 'status-inactive'}`}>
                    {c.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            {editingId === c.id ? null : (
              <div className="flex gap-2 mt-4">
                <button type="button" className="glass-button text-sm px-3 py-1" onClick={() => startEdit(c)}>Edit</button>
                <button
                  type="button"
                  className="glass-button text-sm px-3 py-1"
                  onClick={() => navigate('/users', { state: { openForm: true, presetRole: 'CLIENT', presetClientId: c.id, presetName: c.name, presetEmail: c.email || '' } })}
                >
                  Create Login
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {editingId ? (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal" style={{ width: 'min(860px, 100%)' }}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold">Edit Client</h3>
              <button type="button" className="glass-button" onClick={cancelEdit}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2">Name</label>
                  <input name="name" value={editData.name} onChange={handleEditChange} className="glass-input w-full" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Company</label>
                  <input name="company" value={editData.company} onChange={handleEditChange} className="glass-input w-full" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input type="email" name="email" value={editData.email} onChange={handleEditChange} className="glass-input w-full" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <input name="phone" value={editData.phone} onChange={handleEditChange} className="glass-input w-full" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Status</label>
                  <select name="is_active" value={editData.is_active} onChange={handleEditChange} className="glass-select w-full">
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="glass-button" onClick={cancelEdit}>Cancel</button>
                <button type="button" className="glass-button primary" onClick={() => saveEdit(editingId)}>Save</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {clients.length === 0 && (
        <div className="text-center p-8 glass-card">
          <p className="text-secondary">No clients found</p>
        </div>
      )}
    </div>
  );
};

export default Clients;
